/**
 * Message thread state for one WhatsApp conversation (FE-06).
 *
 * Initial load and "load older" pagination hit GET .../thread directly.
 * Live updates (wa_message_new / wa_status_update) for the OPEN conversation
 * re-fetch just the latest page and merge: messages not yet held locally are
 * appended, messages already held get their status fields patched in place
 * (so a delivered/read tick updates without disturbing scroll position).
 *
 * Known v1 limitation: the merge only looks at the single latest page, so if
 * far more messages arrive between two live events than the page size, the
 * gap in the middle is dropped until the next full reload. Normal chat
 * cadence never approaches that, so this is an acceptable approximation
 * rather than something worth a cursor-diffing scheme in v1.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  whatsappInboxService,
  WhatsAppMessage,
} from '@/services/integrations/whatsappInboxService';
import { useWhatsAppSocketEvents } from './useWhatsAppSocket';

const DEGRADED_POLL_MS = 8_000;
const THREAD_PAGE_SIZE = 50;

export function useConversationThread(conversationUuid: string | null) {
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const loadInitial = useCallback(async () => {
    if (!conversationUuid) {
      setMessages([]);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const thread = await whatsappInboxService.getThread(conversationUuid, { limit: THREAD_PAGE_SIZE });
      setMessages(thread?.messages ?? []);
      setHasMore(thread?.has_more ?? false);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [conversationUuid]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (!conversationUuid || loadingMore || !hasMore || messagesRef.current.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = messagesRef.current[0];
      const thread = await whatsappInboxService.getThread(conversationUuid, {
        before: oldest.uuid, limit: THREAD_PAGE_SIZE,
      });
      if (thread) {
        setMessages((prev) => [...thread.messages, ...prev]);
        setHasMore(thread.has_more);
      }
    } catch {
      // Leave hasMore as-is — a transient failure shouldn't hide the "load more" affordance.
    } finally {
      setLoadingMore(false);
    }
  }, [conversationUuid, hasMore]);

  const refreshTail = useCallback(async () => {
    if (!conversationUuid) return;
    try {
      const thread = await whatsappInboxService.getThread(conversationUuid, { limit: THREAD_PAGE_SIZE });
      if (!thread) return;
      setMessages((prev) => {
        const byUuid = new Map(thread.messages.map((m) => [m.uuid, m]));
        const merged = prev.map((m) => {
          const fresh = byUuid.get(m.uuid);
          if (!fresh) return m;
          byUuid.delete(m.uuid);
          return { ...m, status: fresh.status, status_at: fresh.status_at,
                   error_code: fresh.error_code, error_title: fresh.error_title };
        });
        const newOnes = thread.messages.filter((m) => byUuid.has(m.uuid));
        return newOnes.length ? [...merged, ...newOnes] : merged;
      });
    } catch {
      /* a missed live refresh isn't fatal — the next event or poll catches up */
    }
  }, [conversationUuid]);

  const { degraded } = useWhatsAppSocketEvents(
    useCallback((event) => {
      if (event.conversation_uuid === conversationUuid) refreshTail();
    }, [conversationUuid, refreshTail]),
  );

  useEffect(() => {
    if (!degraded || !conversationUuid) return;
    const id = setInterval(refreshTail, DEGRADED_POLL_MS);
    return () => clearInterval(id);
  }, [degraded, conversationUuid, refreshTail]);

  return { messages, hasMore, loading, loadingMore, error, loadMore, refresh: refreshTail };
}
